import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Image,
  Pressable,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { PageContainer, Text, Theme } from '../../components/ui';
import { PLACEHOLDER_ARTICLES } from '../../constants/placeholderArticles';
import RecommendationPill from '../MentorChat/components/RecommendationPill';
import { BookService } from '../../services/bookService';
import { BookRepository } from '../../repositories/bookRepository';

interface ArticleDetailScreenProps {
  route: {
    params: {
      articleId: string;
    };
  };
}

/**
 * Article Detail Screen
 * Displays full article content with clickable book/insight references
 */
const ArticleDetailScreen: React.FC = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { articleId } = (route.params as any) || {};
  
  const [bookTitles, setBookTitles] = useState<Record<string, string>>({});
  const [bookCovers, setBookCovers] = useState<Record<string, string>>({});
  const [insightTitles, setInsightTitles] = useState<Record<string, string>>({});

  // Find the article
  const article = useMemo(
    () => PLACEHOLDER_ARTICLES.find((a) => a.id === articleId),
    [articleId]
  );

  // Fetch book and insight titles using standard services
  useEffect(() => {
    if (!article) return;

    const fetchTitles = async () => {
      console.log('[ArticleDetail] Fetching book and insight data...');
      
      // Fetch book titles and covers using BookService
      const bookPromises = article.bookReferences.map(async (ref) => {
        try {
          const result = await BookService.getBookDetail(ref.bookId, 'en');
          if (result.success && result.data) {
            const book = result.data.book;
            return {
              id: ref.bookId,
              title: book.title,
              cover: book.coverImageUrl || book.coverImageFullUrl,
            };
          }
        } catch (error) {
          console.error(`[ArticleDetail] Error fetching book ${ref.bookId}:`, error);
        }
        return null;
      });

      // Fetch insight titles using BookRepository
      const insightPromises = article.insightReferences.map(async (ref) => {
        try {
          const insight = await BookRepository.getInsightById(ref.insightId, 'en');
          if (insight) {
            return { id: ref.insightId, title: insight.title };
          }
        } catch (error) {
          console.error(`[ArticleDetail] Error fetching insight ${ref.insightId}:`, error);
        }
        return null;
      });

      const books = await Promise.all(bookPromises);
      const insights = await Promise.all(insightPromises);

      const bookTitlesMap: Record<string, string> = {};
      const bookCoversMap: Record<string, string> = {};
      books.forEach((book) => {
        if (book) {
          bookTitlesMap[book.id] = book.title;
          bookCoversMap[book.id] = book.cover;
          console.log('[ArticleDetail] Book loaded:', book.id, book.title, 'Cover:', book.cover);
        }
      });

      const insightTitlesMap: Record<string, string> = {};
      insights.forEach((insight) => {
        if (insight) {
          insightTitlesMap[insight.id] = insight.title;
          console.log('[ArticleDetail] Insight loaded:', insight.id, insight.title);
        }
      });

      console.log('[ArticleDetail] Loaded', Object.keys(bookTitlesMap).length, 'books and', Object.keys(insightTitlesMap).length, 'insights');

      setBookTitles(bookTitlesMap);
      setBookCovers(bookCoversMap);
      setInsightTitles(insightTitlesMap);
    };

    fetchTitles();
  }, [article]);

  if (!article) {
    return (
      <PageContainer>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Article not found</Text>
        </View>
      </PageContainer>
    );
  }

  // Render content with inline clickable book/insight references
  const renderContent = () => {
    const paragraphs = article.content.split('\n\n').filter((p) => p.trim());

    return (
      <View style={styles.contentContainer}>
        {paragraphs.map((paragraph, pIndex) => {
          // Parse paragraph for references
          const parts: Array<{ text: string; type?: 'book' | 'insight'; id?: string }> = [];
          let lastIndex = 0;
          let currentText = paragraph;

          // Find all [Book:XX] and [Insight:XXX] markers
          const bookRegex = /\[Book:(\d+)\]/g;
          const insightRegex = /\[Insight:(\d+)\]/g;
          
          const allMatches: Array<{ index: number; length: number; type: 'book' | 'insight'; id: string }> = [];
          
          let match;
          while ((match = bookRegex.exec(paragraph)) !== null) {
            allMatches.push({
              index: match.index,
              length: match[0].length,
              type: 'book',
              id: match[1],
            });
          }
          
          while ((match = insightRegex.exec(paragraph)) !== null) {
            allMatches.push({
              index: match.index,
              length: match[0].length,
              type: 'insight',
              id: match[1],
            });
          }
          
          // Sort by index
          allMatches.sort((a, b) => a.index - b.index);
          
          // Build parts array
          allMatches.forEach((m) => {
            if (m.index > lastIndex) {
              parts.push({ text: paragraph.substring(lastIndex, m.index) });
            }
            
            const title = m.type === 'book' 
              ? (bookTitles[m.id] || `Book ${m.id}`)
              : (insightTitles[m.id] || `Insight ${m.id}`);
            
            parts.push({ text: `"${title}"`, type: m.type, id: m.id });
            lastIndex = m.index + m.length;
          });
          
          if (lastIndex < paragraph.length) {
            parts.push({ text: paragraph.substring(lastIndex) });
          }
          
          // If no references found, just render plain text
          if (parts.length === 0) {
            return (
              <Text key={pIndex} style={styles.contentText}>
                {paragraph.trim()}
              </Text>
            );
          }
          
          // Render with clickable references
          return (
            <Text key={pIndex} style={styles.contentText}>
              {parts.map((part, partIndex) => {
                if (part.type && part.id) {
                  return (
                    <Text
                      key={partIndex}
                      style={[
                        styles.referenceLink,
                        part.type === 'book' ? styles.bookLink : styles.insightLink,
                      ]}
                      onPress={() => handleReferencePress(part.type!, part.id!)}
                    >
                      {part.text}
                    </Text>
                  );
                }
                return <Text key={partIndex}>{part.text}</Text>;
              })}
            </Text>
          );
        })}
      </View>
    );
  };

  const handleReferencePress = (type: 'book' | 'insight', id: string) => {
    if (type === 'book') {
      const bookId = id.includes('_') ? id.split('_')[0] : id;
      (navigation as any).navigate('BookDetail', { 
        bookId, 
        languageCode: 'en',
        fromRoute: 'ArticleDetail',
      });
    } else {
      // For insights, we need to pass insightIds array and currentIndex
      // Get all insight IDs from the article
      const insightIds = article.insightReferences.map(ref => ref.insightId);
      const currentIndex = insightIds.indexOf(id);
      
      (navigation as any).navigate('InsightDetail', {
        insightIds,
        currentIndex: currentIndex >= 0 ? currentIndex : 0,
        isMixedInsights: true,
        fromRoute: 'ArticleDetail',
      });
    }
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header Image */}
        <View style={styles.imageContainer}>
          <Image
            source={{ uri: article.imageUrl }}
            style={styles.headerImage}
            resizeMode="cover"
          />
          <Pressable style={styles.backButton} onPress={handleBackPress}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </Pressable>
        </View>

        {/* Article Content */}
        <View style={styles.contentWrapper}>
          <Text style={styles.title}>{article.title}</Text>
          <Text style={styles.date}>
            {new Date(article.createdAt * 1000).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Text>

          {renderContent()}

          {/* Referenced Books and Insights */}
          {(article.bookReferences.length > 0 || article.insightReferences.length > 0) && (
            <View style={styles.referencesSection}>
              <Text style={styles.referencesTitle}>Books & Insights Referenced</Text>
              <Text style={styles.referencesSubtitle}>
                Tap to explore the ideas mentioned in this article
              </Text>
              
              {/* Books */}
              {article.bookReferences.length > 0 && (
                <View style={styles.referenceCategory}>
                  <View style={styles.categoryHeader}>
                    <Ionicons name="book" size={18} color="#E67E22" />
                    <Text style={styles.categoryTitle}>Books</Text>
                  </View>
                  {article.bookReferences.map((ref) => (
                    <RecommendationPill
                      key={ref.bookId}
                      type="book"
                      id={ref.bookId}
                      title={bookTitles[ref.bookId] || ref.title}
                      coverImageUrl={bookCovers[ref.bookId]}
                      onPress={handleReferencePress}
                    />
                  ))}
                </View>
              )}

              {/* Ideas */}
              {article.insightReferences.length > 0 && (
                <View style={styles.referenceCategory}>
                  <View style={styles.categoryHeader}>
                    <Ionicons name="bulb" size={18} color="#3498DB" />
                    <Text style={styles.categoryTitle}>Key Ideas</Text>
                  </View>
                  {article.insightReferences.map((ref) => (
                    <RecommendationPill
                      key={ref.insightId}
                      type="insight"
                      id={ref.insightId}
                      title={insightTitles[ref.insightId] || ref.title}
                      onPress={handleReferencePress}
                    />
                  ))}
                </View>
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  imageContainer: {
    width: '100%',
    height: 250,
    position: 'relative',
  },
  headerImage: {
    width: '100%',
    height: '100%',
  },
  backButton: {
    position: 'absolute',
    top: 50,
    left: Theme.spacing.lg,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentWrapper: {
    padding: Theme.spacing.lg,
  },
  title: {
    fontSize: 28,
    fontFamily: Theme.typography.fontFamily.bold,
    color: Theme.colors.textPrimary,
    lineHeight: 36,
    marginBottom: Theme.spacing.sm,
  },
  date: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    marginBottom: Theme.spacing.xl,
  },
  contentContainer: {
    marginBottom: Theme.spacing.xl,
  },
  contentText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    lineHeight: 26,
    marginBottom: Theme.spacing.lg,
  },
  referenceLink: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    textDecorationLine: 'underline',
  },
  bookLink: {
    color: '#E67E22', // Orange for books
  },
  insightLink: {
    color: '#3498DB', // Blue for insights
  },
  referencesSection: {
    marginTop: Theme.spacing.xl,
    paddingTop: Theme.spacing.xl,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
  },
  referencesTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.bold,
    color: Theme.colors.textPrimary,
    marginBottom: Theme.spacing.xs,
  },
  referencesSubtitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    marginBottom: Theme.spacing.lg,
  },
  referenceCategory: {
    marginBottom: Theme.spacing.lg,
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Theme.spacing.sm,
    gap: Theme.spacing.xs,
  },
  categoryTitle: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Theme.spacing.xl,
  },
  errorText: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
  },
});

export default ArticleDetailScreen;
